import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import CallHistoryModal from '../components/CallHistoryModal';
import MissedNotRecalledModal from '../components/MissedNotRecalledModal';
import {
    PhoneCall,
    PhoneIncoming,
    PhoneOutgoing,
    PhoneMissed,
    Search as SearchIcon,
    Loader2,
    Clock,
    Filter,
    ChevronRight,
    User,
    UserCheck,
    AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
    { id: 'today', label: "Aujourd'hui" },
    { id: 'week',  label: 'Cette semaine' },
    { id: 'month', label: 'Ce mois' },
    { id: 'all',   label: 'Tout' },
];

const DIRECTIONS = [
    { id: 'missed',   label: 'Manqués',  icon: PhoneMissed,   color: 'text-red-500',     bg: 'bg-red-50' },
    { id: 'all',      label: 'Tous',     icon: PhoneCall,     color: 'text-gray-600',    bg: 'bg-gray-50' },
    { id: 'incoming', label: 'Entrants', icon: PhoneIncoming, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { id: 'outgoing', label: 'Sortants', icon: PhoneOutgoing, color: 'text-blue-600',    bg: 'bg-blue-50' },
];

function formatDuration(s) {
    if (!s || s <= 0) return '0:00';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
}

function formatDateTime(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        const today = new Date();
        const sameDay = d.toDateString() === today.toDateString();
        const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        if (sameDay) return time;
        return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} ${time}`;
    } catch { return '—'; }
}

function CallRow({ call, navigate, onShowHistory }) {
    const isOut = call.direction === 'outgoing';
    const isMissed = !!call.missed;
    const Icon = isMissed ? PhoneMissed : (isOut ? PhoneOutgoing : PhoneIncoming);
    const iconColor = isMissed ? 'text-red-500' : (isOut ? 'text-blue-500' : 'text-emerald-500');
    const iconBg    = isMissed ? 'bg-red-50'   : (isOut ? 'bg-blue-50'   : 'bg-emerald-50');

    const m = call.match;
    const onClickMatch = () => {
        if (!m) return;
        if (m.type === 'client' && m.id) navigate(`/clients/${m.id}`);
        else if (m.type === 'prospect' && m.id) navigate(`/prospects/${m.id}`);
    };

    return (
        <div className={`grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 ${isMissed ? 'bg-red-50/40' : ''}`} data-testid={`call-row-${call.cdr_id}`}>
            <div className="col-span-1 flex items-center justify-center">
                <div className={`w-9 h-9 rounded-full ${iconBg} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
            </div>
            <div className="col-span-2 text-xs text-gray-600">
                <Clock className="w-3 h-3 inline mr-1 text-gray-400" />
                {formatDateTime(call.start_datetime)}
            </div>
            <div className="col-span-3 truncate">
                <button
                    onClick={() => call.distant_number && onShowHistory(call.distant_number)}
                    disabled={!call.distant_number}
                    className="text-sm font-mono text-[#1B365D] hover:text-[#C9A962] hover:underline transition-colors disabled:cursor-default disabled:hover:text-[#1B365D] disabled:hover:no-underline"
                    title={call.distant_number ? "Voir l'historique de ce numéro" : ''}
                    data-testid={`call-number-${call.cdr_id}`}
                >
                    {call.distant_number || '—'}
                </button>
            </div>
            <div className="col-span-3 min-w-0">
                {m ? (
                    <button
                        onClick={onClickMatch}
                        className="inline-flex items-center gap-1.5 text-sm text-[#1B365D] font-semibold hover:text-[#C9A962] transition-colors truncate w-full"
                        data-testid={`call-match-${call.cdr_id}`}
                    >
                        {m.type === 'client'
                            ? <UserCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            : <User      className="w-3.5 h-3.5 text-purple-500 shrink-0" />}
                        <span className="truncate">{m.nom}</span>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${m.type === 'client' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`}>
                            {m.type === 'client' ? 'Client' : 'Prospect'}
                        </span>
                        <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />
                    </button>
                ) : (
                    <span className="text-xs text-gray-400 italic">—</span>
                )}
            </div>
            <div className="col-span-2 text-xs text-gray-500 font-mono truncate">
                {call.phone_line || '—'}
            </div>
            <div className="col-span-1 text-xs text-right">
                {isMissed
                    ? <span className="text-red-500 font-semibold">Manqué</span>
                    : <span className="text-gray-600">{formatDuration(call.duration_seconds)}</span>}
            </div>
        </div>
    );
}

export default function JournalAppels() {
    const navigate = useNavigate();
    const [period, setPeriod] = useState('week');
    const [direction, setDirection] = useState('missed'); // user choice = default missed
    const [q, setQ] = useState('');
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historyNumber, setHistoryNumber] = useState(null);
    const [showMissedModal, setShowMissedModal] = useState(false);
    const [missedNotRecalledCount, setMissedNotRecalledCount] = useState(0);
    const debounceRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ period, direction, limit: '300' });
            if (q.trim()) params.set('q', q.trim());
            const res = await axios.get(`${API_URL}/api/calls/journal?${params.toString()}`, { withCredentials: true });
            setCalls(res.data || []);
        } catch {
            setCalls([]);
        } finally {
            setLoading(false);
        }
    }, [period, direction, q]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(load, 200);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
    }, [load]);

    // Fetch missed-not-recalled count once + when modal closes (might have changed).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API_URL}/api/calls/missed-not-recalled?period=month`, { withCredentials: true });
                if (!cancelled) setMissedNotRecalledCount((res.data?.items || []).length);
            } catch { /* silently ignore */ }
        })();
        return () => { cancelled = true; };
    }, [showMissedModal]);

    // KPIs
    const kpiTotal    = calls.length;
    const kpiMissed   = calls.filter(c => c.missed).length;
    const kpiAnswered = calls.filter(c => !c.missed).length;
    const kpiTotalSec = calls.reduce((acc, c) => acc + (c.duration_seconds || 0), 0);
    const kpiTotalDur = (() => {
        const h = Math.floor(kpiTotalSec / 3600);
        const m = Math.floor((kpiTotalSec % 3600) / 60);
        return h > 0 ? `${h}h ${m}min` : `${m}min`;
    })();

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#1B365D] flex items-center gap-2">
                        <PhoneCall className="w-6 h-6 text-[#C9A962]" />
                        Journal d'appel
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Tous les appels entrants, sortants et manqués de vos lignes téléphoniques.
                    </p>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                        <p className="text-[11px] uppercase text-gray-400 tracking-wide">Total</p>
                        <p className="text-2xl font-bold text-[#1B365D] mt-1">{kpiTotal}</p>
                    </div>
                    <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                        <p className="text-[11px] uppercase text-red-500 tracking-wide font-semibold">Manqués</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">{kpiMissed}</p>
                    </div>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                        <p className="text-[11px] uppercase text-emerald-600 tracking-wide font-semibold">Répondus</p>
                        <p className="text-2xl font-bold text-emerald-700 mt-1">{kpiAnswered}</p>
                    </div>
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                        <p className="text-[11px] uppercase text-gray-400 tracking-wide">Durée totale</p>
                        <p className="text-2xl font-bold text-[#1B365D] mt-1">{kpiTotalDur}</p>
                    </div>
                    <button
                        onClick={() => setShowMissedModal(true)}
                        className={`rounded-xl border p-4 text-left transition-all hover:shadow-md ${missedNotRecalledCount > 0 ? 'border-orange-300 bg-orange-50 hover:bg-orange-100' : 'border-gray-200 bg-white'}`}
                        data-testid="kpi-missed-not-recalled"
                    >
                        <p className={`text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1 ${missedNotRecalledCount > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                            <AlertCircle className="w-3 h-3" />
                            À rappeler
                        </p>
                        <p className={`text-2xl font-bold mt-1 ${missedNotRecalledCount > 0 ? 'text-orange-700' : 'text-[#1B365D]'}`}>
                            {missedNotRecalledCount}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Cliquer pour voir la liste</p>
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                    <div className="flex flex-wrap gap-1 bg-white border border-gray-200 rounded-lg p-1" data-testid="direction-filter">
                        {DIRECTIONS.map(d => {
                            const DIcon = d.icon;
                            const active = direction === d.id;
                            return (
                                <button
                                    key={d.id}
                                    onClick={() => setDirection(d.id)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${active ? `${d.color} ${d.bg}` : 'text-gray-500 hover:bg-gray-50'}`}
                                    data-testid={`direction-${d.id}`}
                                >
                                    <DIcon className="w-3.5 h-3.5" />
                                    {d.label}
                                </button>
                            );
                        })}
                    </div>
                    <div className="flex flex-wrap gap-1 bg-white border border-gray-200 rounded-lg p-1" data-testid="period-filter">
                        {PERIODS.map(p => (
                            <button
                                key={p.id}
                                onClick={() => setPeriod(p.id)}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${period === p.id ? 'bg-[#C9A962]/10 text-[#C9A962]' : 'text-gray-500 hover:bg-gray-50'}`}
                                data-testid={`period-${p.id}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="relative flex-1 min-w-0">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            value={q}
                            onChange={e => setQ(e.target.value)}
                            placeholder="Rechercher un numéro…"
                            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C9A962]"
                            data-testid="call-search-input"
                        />
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                    <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-bold uppercase text-gray-500 tracking-wide">
                        <div className="col-span-1 text-center">Type</div>
                        <div className="col-span-2"><Clock className="w-3 h-3 inline mr-1" />Date</div>
                        <div className="col-span-3">Numéro</div>
                        <div className="col-span-3">Prospect / Client</div>
                        <div className="col-span-2">Ligne</div>
                        <div className="col-span-1 text-right">Durée</div>
                    </div>
                    {loading && (
                        <div className="px-4 py-12 flex items-center justify-center text-gray-400 gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Chargement…
                        </div>
                    )}
                    {!loading && calls.length === 0 && (
                        <div className="px-4 py-12 text-center text-sm text-gray-400">
                            <Filter className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p className="font-medium text-gray-500 mb-1">Aucun appel pour ces filtres</p>
                            {period !== 'all' && (
                                <button
                                    type="button"
                                    onClick={() => setPeriod('all')}
                                    className="text-xs text-[#1B365D] hover:underline mt-1"
                                    data-testid="show-all-calls"
                                >
                                    Voir tous les appels →
                                </button>
                            )}
                        </div>
                    )}
                    {!loading && calls.map(c => (
                        <CallRow key={c.cdr_id} call={c} navigate={navigate} onShowHistory={setHistoryNumber} />
                    ))}
                </div>

                {!loading && calls.length > 0 && (
                    <p className="text-xs text-gray-400 text-center">
                        {calls.length} appel{calls.length > 1 ? 's' : ''} affiché{calls.length > 1 ? 's' : ''} (limite : 300 derniers)
                    </p>
                )}
            </div>
            <CallHistoryModal
                open={!!historyNumber}
                number={historyNumber || ''}
                onOpenChange={(v) => { if (!v) setHistoryNumber(null); }}
            />
            <MissedNotRecalledModal
                open={showMissedModal}
                onOpenChange={setShowMissedModal}
                onShowHistory={(num) => {
                    setShowMissedModal(false);
                    setHistoryNumber(num);
                }}
            />
        </Layout>
    );
}
